/**
 * PresentationTimelineEditor
 *
 * Visual timeline editor for physical PDF presentations.
 * Each PDF page becomes a slot; pitch rounds, pauses and other standard
 * meeting blocks can be inserted between pages.
 *
 * Renders inside PhysicalPresentationSetup after a PDF is uploaded.
 */

import React, { useState, useCallback, useRef, useEffect, memo } from 'react';
import type { PresentationSlot, PresentationSlotType } from '../networkClubTypes';
import { createInsertSlot, reindexSlots } from '../classifyPdfPages';
import type { PdfPageInfo } from '../pdfPageExtractor';

// ── Slot-type styling ────────────────────────────────────────────────────────

const TYPE_BADGE: Record<PresentationSlotType, { bg: string; text: string; icon: string }> = {
  pdf_page:     { bg: 'bg-slate-100',   text: 'text-slate-600',  icon: '📄' },
  pitch_leden:  { bg: 'bg-amber-50',    text: 'text-amber-700',  icon: '🎤' },
  pitch_gasten: { bg: 'bg-purple-50',   text: 'text-purple-700', icon: '🎙️' },
  presentatie:  { bg: 'bg-blue-50',     text: 'text-blue-700',   icon: '📊' },
  pauze:        { bg: 'bg-green-50',    text: 'text-green-700',  icon: '☕' },
  intro:        { bg: 'bg-cyan-50',     text: 'text-cyan-700',   icon: '👋' },
  einde:        { bg: 'bg-rose-50',     text: 'text-rose-700',   icon: '🏁' },
};

const INSERTABLE_TYPES: { type: PresentationSlotType; label: string }[] = [
  { type: 'pitch_leden',  label: 'Pitch leden' },
  { type: 'pitch_gasten', label: 'Pitch gasten' },
  { type: 'presentatie',  label: 'Presentatie' },
  { type: 'pauze',        label: 'Pauze' },
  { type: 'intro',        label: 'Intro' },
  { type: 'einde',        label: 'Einde bijeenkomst' },
];

// ── Props ────────────────────────────────────────────────────────────────────

interface PresentationTimelineEditorProps {
  slots: PresentationSlot[];
  thumbnails: Map<number, string>;
  onChange: (slots: PresentationSlot[]) => void;
  primaryColor: string;
  accentColor: string;
}

// ── Single slot row ──────────────────────────────────────────────────────────

const SlotRow = memo(function SlotRow({
  slot,
  thumb,
  onToggle,
  onLabelChange,
  onDurationChange,
  onDelete,
  primaryColor,
}: {
  slot: PresentationSlot;
  thumb?: string;
  onToggle: (id: string) => void;
  onLabelChange: (id: string, label: string) => void;
  onDurationChange: (id: string, minutes: number) => void;
  onDelete: (id: string) => void;
  primaryColor: string;
}) {
  const badge = TYPE_BADGE[slot.type];
  const isPdfPage = slot.type === 'pdf_page';

  return (
    <div
      className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all ${
        slot.enabled
          ? 'bg-white border-slate-200 shadow-sm'
          : 'bg-slate-50 border-slate-100 opacity-50'
      }`}
    >
      {/* Eye toggle */}
      <button
        type="button"
        onClick={() => onToggle(slot.id)}
        className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center hover:bg-slate-100 transition-colors"
        title={slot.enabled ? 'Overslaan in meeting' : 'Weer activeren'}
      >
        {slot.enabled ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={primaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
        )}
      </button>

      {/* Thumbnail (only for PDF pages) */}
      {isPdfPage && thumb ? (
        <div className="shrink-0 w-16 h-10 rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
          <img src={thumb} alt={`p.${slot.pageNumber}`} className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-base ${badge.bg}`}>
          {badge.icon}
        </div>
      )}

      {/* Label (editable) */}
      <div className="flex-1 min-w-0">
        <input
          type="text"
          value={slot.label}
          onChange={(e) => onLabelChange(slot.id, e.target.value)}
          className="w-full text-[13px] font-semibold text-slate-800 bg-transparent border-none outline-none placeholder:text-slate-400 truncate"
          placeholder="Label..."
        />
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${badge.bg} ${badge.text}`}>
            {slot.type.replace('_', ' ')}
          </span>
          {isPdfPage && slot.pageNumber != null && (
            <span className="text-[10px] text-slate-400">p.{slot.pageNumber}</span>
          )}
          {slot.aiConfidence != null && slot.aiConfidence > 0 && (
            <span className="text-[10px] text-slate-400" title={`AI confidence: ${Math.round(slot.aiConfidence * 100)}%`}>
              AI {Math.round(slot.aiConfidence * 100)}%
            </span>
          )}
        </div>
      </div>

      {/* Duration */}
      <div className="shrink-0 flex items-center gap-1">
        <input
          type="number"
          min={0}
          max={120}
          value={slot.durationMinutes}
          onChange={(e) => onDurationChange(slot.id, Math.max(0, parseInt(e.target.value) || 0))}
          className="w-12 h-8 text-center text-[13px] font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-slate-400 transition-colors"
        />
        <span className="text-[11px] text-slate-400">min</span>
      </div>

      {/* Delete (only non-PDF slots) */}
      {!isPdfPage && (
        <button
          type="button"
          onClick={() => onDelete(slot.id)}
          className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
          title="Verwijderen"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-2 14H7L5 6" />
            <path d="M10 11v6" />
            <path d="M14 11v6" />
          </svg>
        </button>
      )}
    </div>
  );
});

// ── Insert bar between slots ─────────────────────────────────────────────────

const InsertBar = memo(function InsertBar({
  afterOrder,
  onInsert,
  accentColor,
}: {
  afterOrder: number;
  onInsert: (type: PresentationSlotType, afterOrder: number) => void;
  accentColor: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative flex items-center justify-center py-0.5 group">
      <div className="absolute inset-x-4 h-px bg-slate-200 group-hover:bg-slate-300 transition-colors" />
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative z-10 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm hover:scale-110 transition-transform"
        style={{ background: accentColor }}
        title="Slot invoegen"
      >
        +
      </button>
      {open && (
        <div className="absolute top-8 left-1/2 -translate-x-1/2 z-30 bg-white rounded-xl shadow-lg border border-slate-200 py-1.5 min-w-[180px] animate-in fade-in slide-in-from-top-1">
          {INSERTABLE_TYPES.map((t) => {
            const b = TYPE_BADGE[t.type];
            return (
              <button
                key={t.type}
                type="button"
                onClick={() => { onInsert(t.type, afterOrder); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-2 transition-colors"
              >
                <span className={`w-6 h-6 rounded flex items-center justify-center text-xs ${b.bg}`}>{b.icon}</span>
                {t.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
});

// ── Main component ───────────────────────────────────────────────────────────

const PresentationTimelineEditor: React.FC<PresentationTimelineEditorProps> = ({
  slots,
  thumbnails,
  onChange,
  primaryColor,
  accentColor,
}) => {
  const sorted = [...slots].sort((a, b) => a.order - b.order);

  const totalMinutes = sorted
    .filter((s) => s.enabled)
    .reduce((sum, s) => sum + s.durationMinutes, 0);

  const handleToggle = useCallback((id: string) => {
    onChange(slots.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s)));
  }, [slots, onChange]);

  const handleLabelChange = useCallback((id: string, label: string) => {
    onChange(slots.map((s) => (s.id === id ? { ...s, label } : s)));
  }, [slots, onChange]);

  const handleDurationChange = useCallback((id: string, minutes: number) => {
    onChange(slots.map((s) => (s.id === id ? { ...s, durationMinutes: minutes } : s)));
  }, [slots, onChange]);

  const handleDelete = useCallback((id: string) => {
    onChange(reindexSlots(slots.filter((s) => s.id !== id)));
  }, [slots, onChange]);

  const handleInsert = useCallback((type: PresentationSlotType, afterOrder: number) => {
    const newSlot = createInsertSlot(type, afterOrder);
    onChange(reindexSlots([...slots, newSlot]));
  }, [slots, onChange]);

  const enabledCount = sorted.filter((s) => s.enabled).length;
  const skippedCount = sorted.length - enabledCount;

  return (
    <div className="flex flex-col gap-1">
      {/* Summary bar */}
      <div className="flex items-center justify-between px-1 py-2">
        <div className="flex items-center gap-3">
          <span className="text-[12px] font-bold text-slate-700">
            {sorted.length} slots
          </span>
          {skippedCount > 0 && (
            <span className="text-[11px] text-slate-400">
              ({skippedCount} overgeslagen)
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={primaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span className="text-[13px] font-bold" style={{ color: primaryColor }}>
            ~{totalMinutes} min
          </span>
        </div>
      </div>

      {/* Slot list */}
      <div className="flex flex-col">
        {/* Insert bar at the very top */}
        <InsertBar afterOrder={-1} onInsert={handleInsert} accentColor={accentColor} />

        {sorted.map((slot) => (
          <React.Fragment key={slot.id}>
            <SlotRow
              slot={slot}
              thumb={slot.pageNumber != null ? thumbnails.get(slot.pageNumber) : undefined}
              onToggle={handleToggle}
              onLabelChange={handleLabelChange}
              onDurationChange={handleDurationChange}
              onDelete={handleDelete}
              primaryColor={primaryColor}
            />
            <InsertBar
              afterOrder={slot.order}
              onInsert={handleInsert}
              accentColor={accentColor}
            />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default PresentationTimelineEditor;
